import { useState } from "react";
import { toast } from "sonner";
import { Send, CalendarClock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PACKAGE_TYPES, URGENCY_LEVELS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function FieldLabel({ htmlFor, children }) {
    return (
        <Label
            htmlFor={htmlFor}
            className="text-[10px] tracking-[0.25em] uppercase text-brand-black/70 mb-2 block"
        >
            {children}
        </Label>
    );
}

function AddressFields({ idPrefix, testidPrefix, title, values, onChange }) {
    return (
        <div className="md:col-span-2 bg-white/60 border border-brand-line p-5 md:p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-gold-deep mb-5">
                {title}
            </div>
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <FieldLabel htmlFor={`${idPrefix}-street`}>Street Number &amp; Name</FieldLabel>
                    <Input
                        id={`${idPrefix}-street`}
                        data-testid={`${testidPrefix}-street`}
                        required
                        placeholder="e.g. 1438 South Grand Blvd, Suite 200"
                        value={values.street}
                        onChange={(e) => onChange("street", e.target.value)}
                        className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                    <div className="sm:col-span-3">
                        <FieldLabel htmlFor={`${idPrefix}-city`}>City</FieldLabel>
                        <Input
                            id={`${idPrefix}-city`}
                            data-testid={`${testidPrefix}-city`}
                            required
                            placeholder="e.g. St. Louis"
                            value={values.city}
                            onChange={(e) => onChange("city", e.target.value)}
                            className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                        />
                    </div>
                    <div className="sm:col-span-1">
                        <FieldLabel htmlFor={`${idPrefix}-state`}>State</FieldLabel>
                        <Input
                            id={`${idPrefix}-state`}
                            data-testid={`${testidPrefix}-state`}
                            required
                            maxLength={2}
                            placeholder="MO"
                            value={values.state}
                            onChange={(e) => onChange("state", e.target.value.toUpperCase())}
                            className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12 uppercase"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <FieldLabel htmlFor={`${idPrefix}-zip`}>Zip Code</FieldLabel>
                        <Input
                            id={`${idPrefix}-zip`}
                            data-testid={`${testidPrefix}-zip`}
                            required
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="e.g. 63104"
                            value={values.zip}
                            onChange={(e) => onChange("zip", e.target.value)}
                            className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message received. We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
            <div>
                <FieldLabel htmlFor="c-name">Full Name</FieldLabel>
                <Input
                    id="c-name"
                    data-testid="contact-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="c-email">Email</FieldLabel>
                <Input
                    id="c-email"
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="c-phone">Phone</FieldLabel>
                <Input
                    id="c-phone"
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="c-subject">Subject</FieldLabel>
                <Input
                    id="c-subject"
                    data-testid="contact-subject"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div className="md:col-span-2">
                <FieldLabel htmlFor="c-message">Message</FieldLabel>
                <Textarea
                    id="c-message"
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold"
                />
            </div>
            <div className="md:col-span-2">
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-brand-black text-white text-xs tracking-[0.25em] uppercase hover:bg-brand-gold-deep transition-colors disabled:opacity-60"
                >
                    {submitting ? "Sending…" : "Send Message"}
                    <Send size={14} />
                </button>
            </div>
        </form>
    );
}

function BookingForm() {
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        company: "",
        pickup_street: "",
        pickup_city: "",
        pickup_state: "",
        pickup_zip: "",
        dropoff_street: "",
        dropoff_city: "",
        dropoff_state: "",
        dropoff_zip: "",
        pickup_date: "",
        pickup_time: "",
        package_type: "lab_specimen",
        urgency: "standard",
        special_instructions: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const {
                pickup_street, pickup_city, pickup_state, pickup_zip,
                dropoff_street, dropoff_city, dropoff_state, dropoff_zip,
                ...rest
            } = form;
            const payload = {
                ...rest,
                pickup_address: `${pickup_street}, ${pickup_city}, ${pickup_state} ${pickup_zip}`.trim(),
                dropoff_address: `${dropoff_street}, ${dropoff_city}, ${dropoff_state} ${dropoff_zip}`.trim(),
            };
            await api.post("/bookings", payload);
            toast.success("Pickup request received. Our dispatch will confirm shortly.");
            setForm({
                customer_name: "",
                customer_email: "",
                customer_phone: "",
                company: "",
                pickup_street: "",
                pickup_city: "",
                pickup_state: "",
                pickup_zip: "",
                dropoff_street: "",
                dropoff_city: "",
                dropoff_state: "",
                dropoff_zip: "",
                pickup_date: "",
                pickup_time: "",
                package_type: "lab_specimen",
                urgency: "standard",
                special_instructions: "",
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            data-testid="booking-form"
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
            <div>
                <FieldLabel htmlFor="b-name">Contact Name</FieldLabel>
                <Input
                    id="b-name"
                    data-testid="booking-name"
                    required
                    value={form.customer_name}
                    onChange={(e) => set("customer_name", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="b-company">Facility / Company</FieldLabel>
                <Input
                    id="b-company"
                    data-testid="booking-company"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="b-email">Email</FieldLabel>
                <Input
                    id="b-email"
                    data-testid="booking-email"
                    type="email"
                    required
                    value={form.customer_email}
                    onChange={(e) => set("customer_email", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="b-phone">Phone</FieldLabel>
                <Input
                    id="b-phone"
                    data-testid="booking-phone"
                    required
                    value={form.customer_phone}
                    onChange={(e) => set("customer_phone", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <AddressFields
                idPrefix="b-pickup"
                testidPrefix="booking-pickup"
                title="Pickup Address"
                values={{
                    street: form.pickup_street,
                    city: form.pickup_city,
                    state: form.pickup_state,
                    zip: form.pickup_zip,
                }}
                onChange={(k, v) => set(`pickup_${k}`, v)}
            />
            <AddressFields
                idPrefix="b-dropoff"
                testidPrefix="booking-dropoff"
                title="Drop-off Address"
                values={{
                    street: form.dropoff_street,
                    city: form.dropoff_city,
                    state: form.dropoff_state,
                    zip: form.dropoff_zip,
                }}
                onChange={(k, v) => set(`dropoff_${k}`, v)}
            />
            <div>
                <FieldLabel htmlFor="b-date">Pickup Date</FieldLabel>
                <Input
                    id="b-date"
                    data-testid="booking-date"
                    type="date"
                    required
                    value={form.pickup_date}
                    onChange={(e) => set("pickup_date", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="b-time">Pickup Time</FieldLabel>
                <Input
                    id="b-time"
                    data-testid="booking-time"
                    type="time"
                    required
                    value={form.pickup_time}
                    onChange={(e) => set("pickup_time", e.target.value)}
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold h-12"
                />
            </div>
            <div>
                <FieldLabel htmlFor="b-package">Package Type</FieldLabel>
                <Select value={form.package_type} onValueChange={(v) => set("package_type", v)}>
                    <SelectTrigger
                        id="b-package"
                        data-testid="booking-package-type"
                        className="rounded-none border-brand-black/20 h-12"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PACKAGE_TYPES.map((p) => (
                            <SelectItem key={p.value} value={p.value} data-testid={`pkg-opt-${p.value}`}>
                                {p.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <FieldLabel htmlFor="b-urgency">Urgency</FieldLabel>
                <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                    <SelectTrigger
                        id="b-urgency"
                        data-testid="booking-urgency"
                        className="rounded-none border-brand-black/20 h-12"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {URGENCY_LEVELS.map((u) => (
                            <SelectItem key={u.value} value={u.value} data-testid={`urg-opt-${u.value}`}>
                                {u.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="md:col-span-2">
                <FieldLabel htmlFor="b-notes">Special Instructions</FieldLabel>
                <Textarea
                    id="b-notes"
                    data-testid="booking-instructions"
                    rows={4}
                    value={form.special_instructions}
                    onChange={(e) => set("special_instructions", e.target.value)}
                    placeholder="Temperature requirements, access codes, contact at dock, etc."
                    className="rounded-none border-brand-black/20 focus-visible:ring-brand-gold"
                />
            </div>
            <div className="md:col-span-2">
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-submit"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-brand-gold text-brand-black text-xs tracking-[0.25em] uppercase hover:bg-brand-gold-deep hover:text-white transition-colors disabled:opacity-60"
                >
                    {submitting ? "Submitting…" : "Request Pickup"}
                    <CalendarClock size={14} />
                </button>
            </div>
        </form>
    );
}

export default function ContactSection() {
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-brand-paper"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="text-center mb-12 md:mb-16">
                    <div className="inline-flex items-center gap-3 mb-6">
                        <span className="w-10 h-px bg-brand-gold" />
                        <span className="text-[10px] tracking-[0.4em] uppercase text-brand-gold-deep">
                            Get In Touch
                        </span>
                        <span className="w-10 h-px bg-brand-gold" />
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-black leading-tight">
                        Schedule a pickup
                        <br />
                        or start a conversation.
                    </h2>
                </div>

                <div id="booking" className="bg-brand-cream p-6 md:p-12 border border-brand-line">
                    <Tabs defaultValue="booking" className="w-full">
                        <TabsList
                            data-testid="contact-tabs"
                            className="grid w-full grid-cols-2 max-w-md mx-auto mb-10 rounded-none bg-white border border-brand-line p-1 h-auto"
                        >
                            <TabsTrigger
                                value="booking"
                                data-testid="tab-booking"
                                className="rounded-none data-[state=active]:bg-brand-black data-[state=active]:text-white py-3 text-xs tracking-[0.2em] uppercase"
                            >
                                Book Pickup
                            </TabsTrigger>
                            <TabsTrigger
                                value="contact"
                                data-testid="tab-contact"
                                className="rounded-none data-[state=active]:bg-brand-black data-[state=active]:text-white py-3 text-xs tracking-[0.2em] uppercase"
                            >
                                Contact Us
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="booking" className="mt-0">
                            <BookingForm />
                        </TabsContent>
                        <TabsContent value="contact" className="mt-0">
                            <ContactForm />
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </section>
    );
}
